import type { ContractType } from '../hooks/useQueries'

// Contract types form a two-level tree: families (LEASE, INSURANCE, MANDATE…)
// and the sub-types that refine them (bail commercial, décennale…). A sub-type
// carries parent_contract_type_uid; a family carries none. See spec
// 2026-07-16-02-familles-de-contrats-mandat-fourniture-pret.
//
// Picking a family without a sub-type stays valid — contracts.contract_type_id
// is nullable and a contract typed LEASE is perfectly well-formed — so the
// family itself is always offered alongside its children.

export interface ContractTypeGroup {
  family: ContractType
  children: ContractType[]
}

/** Resolves a multilingual names object to the active locale (locale → en → fr → first → code). */
export function contractTypeLabel(type: ContractType, locale: string): string {
  const names = type.names as Record<string, string> | string | undefined
  if (typeof names === 'string') return names || type.code
  if (!names) return type.code
  return names[locale] || names.en || names.fr || Object.values(names)[0] || type.code
}

/**
 * Groups contract types into families with their sub-types, both sorted by
 * localized label.
 *
 * A sub-type whose family is missing from the input (filtered out, or on
 * another page) is promoted to a family of its own rather than dropped — a type
 * the user can legitimately pick must never vanish from the select.
 */
export function groupContractTypes(types: ContractType[], locale: string): ContractTypeGroup[] {
  const byUid = new Map(types.map(t => [t.uid, t]))
  const childrenByParent = new Map<string, ContractType[]>()
  const families: ContractType[] = []

  for (const type of types) {
    const parentUid = type.parent_contract_type_uid
    if (parentUid && byUid.has(parentUid)) {
      const siblings = childrenByParent.get(parentUid) || []
      siblings.push(type)
      childrenByParent.set(parentUid, siblings)
    } else {
      families.push(type)
    }
  }

  const byLabel = (a: ContractType, b: ContractType) =>
    contractTypeLabel(a, locale).localeCompare(contractTypeLabel(b, locale))

  return families.sort(byLabel).map(family => ({
    family,
    children: (childrenByParent.get(family.uid) || []).sort(byLabel),
  }))
}
